/**
 * McNemar's test for paired binary outcomes.
 *
 *                     after: yes   after: no
 *   before: yes           a            b
 *   before: no            c            d
 *
 * Only the discordant pairs b and c carry information about a change. Under H₀,
 * b ~ Binomial(b + c, ½), so:
 *
 * - χ² = (b − c)² / (b + c), 1 df (McNemar, 1947);
 * - χ²_c = (|b − c| − 1)² / (b + c), Edwards's continuity correction — R's
 *   `mcnemar.test` default;
 * - exact p = min(1, 2 · P(X ≤ min(b, c))), the two-sided binomial test that
 *   `scipy.stats.binomtest(b, b + c)` gives for p = ½.
 *
 * The odds ratio of the paired design is b / c (its conditional MLE). Its interval is
 * exact: the Clopper–Pearson interval for π = b / (b + c), mapped through π / (1 − π).
 */

import { binomialCdf, chiSquareUpper } from "@/lib/stats/distributions";
import { invertMonotone, regularizedBeta } from "@/lib/stats/special";

export interface McNemarResult {
  table: [number, number, number, number];
  n: number;
  discordant: number;
  chiSquare: number;
  pChiSquare: number;
  chiSquareCorrected: number;
  pChiSquareCorrected: number;
  pExact: number;
  /** b / c: Infinity when c = 0 < b. */
  oddsRatio: number;
  ciLow: number;
  ciHigh: number;
  level: number;
  /** Proportion "yes" on the first and second measurement. */
  p1: number;
  p2: number;
}

/** Clopper–Pearson bounds for k successes in n trials. */
export function clopperPearson(
  k: number,
  n: number,
  level: number
): [number, number] {
  const alpha = (1 - level) / 2;
  const low =
    k === 0
      ? 0
      : invertMonotone((x) => regularizedBeta(x, k, n - k + 1), alpha, 0, 1);
  const high =
    k === n
      ? 1
      : invertMonotone(
          (x) => regularizedBeta(x, k + 1, n - k),
          1 - alpha,
          0,
          1
        );
  return [low, high];
}

function toOdds(pi: number): number {
  return pi >= 1 ? Number.POSITIVE_INFINITY : pi / (1 - pi);
}

export function mcnemar(
  a: number,
  b: number,
  c: number,
  d: number,
  level = 0.95
): McNemarResult | null {
  const cells = [a, b, c, d];
  if (cells.some((value) => !Number.isInteger(value) || value < 0)) {
    return null;
  }
  const discordant = b + c;
  if (discordant === 0) {
    return null;
  }
  const n = a + b + c + d;
  const chiSquare = (b - c) ** 2 / discordant;
  const corrected = Math.max(0, Math.abs(b - c) - 1) ** 2 / discordant;
  const pExact = Math.min(1, 2 * binomialCdf(Math.min(b, c), discordant, 0.5));
  const [low, high] = clopperPearson(b, discordant, level);
  let oddsRatio = Number.POSITIVE_INFINITY;
  if (c > 0) {
    oddsRatio = b / c;
  }
  return {
    table: [a, b, c, d],
    n,
    discordant,
    chiSquare,
    pChiSquare: chiSquareUpper(chiSquare, 1),
    chiSquareCorrected: corrected,
    pChiSquareCorrected: chiSquareUpper(corrected, 1),
    pExact,
    oddsRatio,
    ciLow: toOdds(low),
    ciHigh: toOdds(high),
    level,
    p1: (a + b) / n,
    p2: (a + c) / n,
  };
}
